import React, { useState, useEffect, useRef, ReactNode } from "react";

interface CircleMenuProps {
  children: ReactNode;
  startAngle?: number; // Starting angle in degrees (-45 default)
  radius?: number; // Distance from center in pixels
  showPulse?: boolean;
  onOpen?: () => void;
  display?: boolean;
}

const CircleMenu: React.FC<CircleMenuProps> = ({
  children,
  startAngle = -45,
  radius = 80,
  showPulse = false,
  onOpen = () => {},
  display = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 200); // Match animation duration
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", closeMenu);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", closeMenu);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    if (isOpen) {
      closeMenu();
    } else {
      setIsOpen(true);
      onOpen();
    }
  };

  // Calculate positions for child items
  const childArray = React.Children.toArray(children);
  const angleStep = childArray.length > 1 ? 60 : 0;

  // Calculate offset so that the middle item (or midpoint) is at startAngle
  const angleOffset = -(angleStep * (childArray.length - 1)) / 2;

  return (
    <div id="circle-menu" className="relative group z-[100]" ref={menuRef}>
      {/* Main circle button with plus sign */}
      <button
        onClick={toggleMenu}
        className={`relative w-14 h-14 rounded-full bg-secondary text-white flex items-center justify-center hover:bg-secondary-dark transition-all duration-300 ${
          isOpen || display ? "rotate-45" : ""
        } ${showPulse ? "animate-pulse-ring" : ""}`}
        aria-label="Open menu"
      >
        {/* Pulse ring effect */}
        {showPulse && (
          <span className="absolute inset-0 rounded-full bg-secondary opacity-75 animate-ping"></span>
        )}
        <svg
          className={"w-6 h-6 relative z-10"}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>

      {/* Menu items positioned in a circle */}
      {(isOpen || display) &&
        childArray.map((child, index) => {
          const angle = startAngle + angleOffset + index * angleStep;
          const angleRad = (angle * Math.PI) / 180;
          const x = radius * Math.cos(angleRad);
          const y = radius * Math.sin(angleRad);

          return (
            <div
              key={index}
              className="absolute"
              style={{
                left: `calc(50% + ${x}px)`,
                top: `calc(50% + ${y}px)`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                className={
                  isClosing
                    ? "animate-circle-menu-close"
                    : "opacity-0 scale-0 animate-circle-menu-open"
                }
                style={{
                  animationDelay: `${index * 50}ms`,
                  animationFillMode: "forwards",
                }}
              >
                {child}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export { CircleMenu };
